import { fetchLeaderboard, LeaderboardEntry } from "../game/api";

export type LeaderboardScope = "drift" | "drift-endless";

export class LeaderboardView {
  private list: HTMLOListElement;
  private highlightId: number | null = null;
  private highlightRank: number | null = null;
  /** Which game scope (timed vs endless) this panel is showing. Switched
   *  from main.ts when the run-mode pill changes. */
  private scope: LeaderboardScope = "drift";

  constructor(list: HTMLOListElement) {
    this.list = list;
  }

  setHighlight(rank: number | null) {
    this.highlightRank = rank;
  }

  setScope(scope: LeaderboardScope) {
    if (this.scope === scope) return;
    this.scope = scope;
    // Drop a stale highlight from the prior scope — its rank doesn't
    // exist on this board.
    this.highlightRank = null;
    this.refresh();
  }

  get currentScope(): LeaderboardScope {
    return this.scope;
  }

  async refresh() {
    // Fetch a deeper slice so we can show the player's neighborhood if their
    // rank lands outside the top 8. 50 rows is a small payload (~3KB JSON)
    // and covers the vast majority of submitted scores for this game.
    const rows = await fetchLeaderboard(50, this.scope);
    this.render(rows);
  }

  private render(rows: LeaderboardEntry[]) {
    if (!rows.length) {
      this.list.innerHTML = `<li class="placeholder">No runs yet — set the first time.</li>`;
      return;
    }
    const top = rows.slice(0, 8);
    const rank = this.highlightRank;
    // If the highlighted rank is outside the top 8 AND we have rows that
    // include it, render top-5 + ellipsis + window-of-5 around the rank.
    if (rank !== null && rank > 8 && rows.some((r) => r.rank === rank)) {
      const head = rows.slice(0, 5);
      const windowStart = Math.max(5, rank - 3);
      const windowEnd = Math.min(rows.length, rank + 2);
      const window = rows.slice(windowStart, windowEnd);
      const headHtml = head.map((r) => this.rowHtml(r)).join("");
      const windowHtml = window.map((r) => this.rowHtml(r)).join("");
      this.list.innerHTML = `${headHtml}<li class="ellipsis">…</li>${windowHtml}`;
      return;
    }
    this.list.innerHTML = top.map((r) => this.rowHtml(r)).join("");
  }

  private rowHtml(r: LeaderboardEntry): string {
    const self = this.highlightRank === r.rank ? " self" : "";
    return `<li class="${self.trim()}">
      <span class="r">#${r.rank}</span>
      <span class="h">${escapeHtml(r.handle)}</span>
      <span class="s">${r.score.toLocaleString()}</span>
    </li>`;
  }
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}
